<i18n>
[
    "reviews__fitTech--label",
    "reviews__employeeBadge--label",
]
</i18n>

<template>
    <div class="c-product-review-card">
        <div
            :class="{
                'c-product-review-card__container': true,
                'c-product-review-card__container--left-image': cardTypeOptions.showFullImage,
            }"
        >
            <template v-if="cardTypeOptions.showLeftColumn">
                <div
                    v-if="cardTypeOptions.showFullImage && reviewDetail.photos.length"
                    class="c-product-review-card__left-image-container"
                >
                    <img
                        :src="fullImageUrl"
                        alt="Review Pic"
                        class="c-product-review-card__left-image"
                    >
                </div>
                <div
                    v-else-if="!cardTypeOptions.showFullImage"
                    class="c-product-review-card__left-column"
                >
                    <ProductReviewUserInfo
                        :profile="reviewDetail.profile"
                        :sizeWorn="reviewDetail.product.sizeDisplayName"
                        :colorWorn="reviewDetail.product.colorDisplayName"
                        :cardType="cardType"
                        class="c-product-review-card__user-info"
                    />
                </div>
            </template>
            <div :class="reviewDetailClasses">
                <ProductReviewUserInfo
                    v-if="cardTypeOptions.showFullImage"
                    :profile="reviewDetail.profile"
                    :sizeWorn="reviewDetail.product.sizeDisplayName"
                    :colorWorn="reviewDetail.product.colorDisplayName"
                    :cardType="cardType"
                />
                <div class="c-product-review-card__review-body-container">
                    <div
                        :class="{
                            'c-product-review-card__review-body': true,
                            'c-product-review-card__review-body--no-padding': cardTypeOptions.showFullImage ||
                                !cardTypeOptions.showLeftColumn,
                        }"
                    >
                        <div
                            :class="{
                                'c-product-review-card__rating-container': !cardTypeOptions.showLeftColumn,
                            }"
                            class="o-flex-vertical-center u-spacer--2"
                        >
                            <label
                                v-if="showFitTechText"
                                class="c-product-review-card__rating-text u-text--gray"
                            >
                                {{ t('reviews__fitTech--label') }}
                            </label>
                            <template v-else>
                                <StarRating
                                    :rating="+reviewDetail.overallRating"
                                    class="c-product-review-card__star"
                                />
                            </template>
                            <div v-if="showPipe" class="c-product-review-card__pipe"></div>
                            <span class="c-product-review-card__rating-date o-text--caption u-text--gray">
                                {{ formattedDate }}
                            </span>
                            <div v-if="showEmployeeBadge" class="c-product-review-card__rating-text--employee">
                                <label class="c-product-review-card__rating-text u-text--gray">
                                    {{ t('reviews__employeeBadge--label') }}
                                </label>
                            </div>
                        </div>
                        <h4 class="u-spacer--1 c-product-review-card__review-title">
                            {{ reviewDetail.reviewTitle }}
                        </h4>
                        <p
                            :class="{
                                'c-product-review-card__review-truncate':
                                    !cardTypeOptions.showFullReviewBody && !showEmployeeBadge,
                                'c-product-review-card__review-truncate--employee':
                                    !cardTypeOptions.showFullReviewBody && showEmployeeBadge,
                                'u-spacer--6': cardTypeOptions.showActionButton,
                            }"
                            class="o-text--caption"
                        >
                            {{ truncatedText }}
                        </p>
                    </div>
                    <div
                        v-if="cardTypeOptions.showThumbnailImage"
                        class="c-product-review-card__review-picture-container"
                    >
                        <UrbnModalLauncher
                            v-if="cardTypeOptions.showThumbnailImage && reviewDetail.photos.length"
                            :modalOptions="{
                                ...MODALS.pdpReviewsCarousel,
                                props: { reviewId: reviewDetail.id },
                            }"
                            launcherComponentClass="c-product-review-card__modal-launcher"
                            type="link"
                        >
                            <img
                                :src="thumbnailImageUrl"
                                alt="Review Pic"
                                class="c-product-review-card__review-picture"
                            >
                        </UrbnModalLauncher>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="cardTypeOptions.showActionButton" class="c-product-review-card__button-container o-text--caption">
            <UrbnModalLauncher
                :modalOptions="MODALS.pdpReviewsDrawer"
                type="link"
                launcherComponentClass="c-product-review-card__button"
            >
                {{ t('reviews__readMore--label') }}
            </UrbnModalLauncher>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// NU_FEATURE: reviewsV3
// https://urbnit.atlassian.net/browse/TYP-34216
import runtimeConfig from '~config/config';

import { isValidDate } from '~coreModules/core/js/utils';

import { MODALS } from '~modules/modals/js/modal-constants';
import { IMAGE_PRESETS } from '~modules/core/js/constants';
import {
    productReviewCardTypesMap,
    PRODUCT_REVIEW_CARD_TYPES,
    PRODUCT_REVIEW_CARD_REVIEW_DETAILS,
    PRODUCT_REVIEW_CARD_REVIEW_LIST,
    PRODUCT_REVIEW_CARD_TOP_REVIEWS,
    PRODUCT_REVIEW_TYPES,
} from '~routes/product/js/product-constants';

import truncateText from '~coreModules/core/js/text-utils';

import StarRating from '~coreModules/core/components/ui/StarRating.vue';
import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';

import ProductReviewUserInfo from '~routes/product/components/ProductReviewUserInfo.vue';

const props = defineProps({
    cardIndex: {
        type: Number,
        required: true,
    },
    reviewDetail: {
        type: Object,
        required: true,
    },
    cardType: {
        type: String,
        required: true,
        validator: value => PRODUCT_REVIEW_CARD_TYPES.indexOf(value) !== -1,
    },
});

const { t, d } = useI18n();

const cardTypeOptions = computed(() => productReviewCardTypesMap[props.cardType]);

const reviewDetailClasses = computed(() => ({
    'c-product-review-card__details': true,
    'c-product-review-card__details--top-reviews': props.cardType === PRODUCT_REVIEW_CARD_TOP_REVIEWS,
    'c-product-review-card__details--list': props.cardType === PRODUCT_REVIEW_CARD_REVIEW_LIST,
    'c-product-review-card__details--carousel': props.cardType === PRODUCT_REVIEW_CARD_REVIEW_DETAILS &&
        props.reviewDetail.photos.length > 0,
    'c-product-review-card__details--with-left-image': props.cardType ===
        PRODUCT_REVIEW_CARD_REVIEW_DETAILS && props.reviewDetail.photos.length > 0,
}));
const thumbnailImageUrl = computed(() => `${props.reviewDetail.photos[0]}?${IMAGE_PRESETS.ugc.square}`);
const fullImageUrl = computed(() => `${props.reviewDetail.photos[0]}?${IMAGE_PRESETS.ugc.regular}`);
// NU_FEATURE: reviewsV3
// https://urbnit.atlassian.net/browse/TYP-34216
// const reviewDate = computed(() => new Date(Date.parse(props.reviewDetail.createdDate));
const reviewDate = computed(() => {
    const date = runtimeConfig.features.reviewsV3 ?
        props.reviewDetail.createdDate : props.reviewDetail.date;
    return new Date(Date.parse(date));
});
const formattedDate = computed(() => (isValidDate(reviewDate.value) ? d(reviewDate.value, 'twoDigits') : ''));
const truncatedText = computed(() => {
    if (!cardTypeOptions.value.showFullReviewBody) {
        return truncateText(props.reviewDetail.reviewBody, 300);
    }

    return props.reviewDetail.reviewBody;
});
const showFitTechText = computed(() => props.reviewDetail.reviewType === PRODUCT_REVIEW_TYPES.FIT_TECH);
const showEmployeeBadge = computed(() => props.reviewDetail.profile?.employee && !showFitTechText.value);
const showPipe = computed(() => !(props.cardType === PRODUCT_REVIEW_CARD_TOP_REVIEWS));
</script>

<style lang="scss">
    .c-product-review-card {
        position: relative;
        background-color: $nu-white;
        margin-bottom: $nu-spacer-1pt5;
        width: 100%;
        white-space: normal;

        &__container {
            display: flex;
            flex-direction: column;

            @include breakpoint(medium) {
                flex-direction: row;
            }

            &--left-image {
                @include breakpoint(medium) {
                    flex-direction: column;
                }

                @include breakpoint(large) {
                    flex-direction: row;
                }
            }
        }

        &__left-image-container {
            position: relative;

            &::before {
                position: relative;
                display: block;
                content: "";
                width: 100%;
                padding-top: 150%;
                background-color: $nu-blush-40;
            }

            @include breakpoint(large) {
                width: 55%;
                aspect-ratio: 3/4;
                overflow-y: hidden;
            }
        }

        &__left-image {
            position: absolute;
            width: 100%;
            max-height: 100%;
            object-fit: contain;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);

            @include breakpoint(large) {
                top: 0;
                left: 0;
                transform: none;
            }
        }

        &__left-column {
            height: 100%;
            padding: $nu-spacer-2 $nu-spacer-2 $nu-spacer-1;

            @include breakpoint(medium) {
                padding: $nu-spacer-2 0 $nu-spacer-2 $nu-spacer-2;
            }

            @include breakpoint(large) {
                padding: $nu-spacer-3;
            }
        }

        &__user-info {
            @include breakpoint(medium) {
                width: 200px
            }
        }

        &__details {
            width: 100%;

            &--top-reviews {
                padding: $nu-spacer-2;

                @include breakpoint(large) {
                    padding: $nu-spacer-3;
                }
            }

            &--list {
                padding: $nu-spacer-2;

                @include breakpoint(medium) {
                    padding: $nu-spacer-3;
                }

                @include breakpoint(large) {
                    padding-left: 0;
                }
            }

            &--carousel {
                padding: $nu-spacer-2;

                @include breakpoint(medium) {
                    padding: $nu-spacer-2 $nu-spacer-2 $nu-spacer-4;
                }

                @include breakpoint(large) {
                    width: 45%;
                    padding: $nu-spacer-6 $nu-spacer-10 $nu-spacer-6 $nu-spacer-6;
                }
            }

            &---with-left-image {
                @include breakpoint(large) {
                    padding: $nu-spacer-6 $nu-spacer-10 $nu-spacer-6;
                }
            }
        }

        &__review-body-container {
            display: flex;
            flex-direction: column;

            @include breakpoint(large) {
                flex-direction: row;
            }
        }

        &__review-body {
            @include breakpoint(large) {
                padding-right: $nu-spacer-8;
                flex-grow: 1
            }

            &--no-padding {
                padding-right: 0;
            }
        }

        &__rating-container {
            justify-content: space-between;
        }

        &__rating-text {
            display: inline;
            background-color: $nu-gray--light;
            padding: $nu-spacer-0pt5 $nu-spacer-1;
        }

        &__pipe {
            @include pipe;
        }

        &__rating-date {
            margin-right: $nu-spacer-1;
        }

        &__review-picture-container {
            display: flex;
            flex-shrink: 0;

            @include breakpoint(medium) {
                width: 232px;
            }
        }

        &__modal-launcher {
            width: 100%;
        }

        &__review-picture {
            width: 100%;
            height: auto;
            display: block;
            margin-top: $nu-spacer-3;

            @include breakpoint(large) {
                margin-top: 0;
            }
        }

        &__button-container {
            position: absolute;
            width: 100%;
            bottom: 0;
            border-top: 1px solid $nu-gray--light;
            text-align: center;
        }

        &__button {
            color: $nu-primary;
            display: inline-block;
            padding: $nu-spacer-1 0;
            border: none;
            background-color: $nu-white;
            width: 100%;
            cursor: pointer;
            outline: none;
            margin: 0;
            height: 40px;
        }

        &__review-truncate,
        &__review-truncate--employee {
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
        }

        &__review-truncate {
            -webkit-line-clamp: 5;
        }

        &__review-truncate--employee {
            -webkit-line-clamp: 3;
        }
    }
</style>
